import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Form,
  Page,
  AppProvider,
  Layout,
  Card,
  Badge,
  Button,
  Collapsible,
  TextField,
  TextStyle,
  FormLayout,
  Link,
  ButtonGroup,
  ResourceItem,
  ResourceList,
  Modal,
  Toast,
  Frame,
  DropZone,
  Tag,
  Tooltip,
  Icon,
  Tabs,
  TextContainer,
  EmptyState,
  Select,
  Stack,
  Popover,
  Autocomplete,
  Subheading,
  ActionList,
  Thumbnail,
  Caption,
} from '@shopify/polaris';
import { useReactToPrint } from 'react-to-print';
import { path, pathOr, prop, propOr, equals, isNil, isEmpty, compose, not } from 'ramda';
import { useQuery, useMutation } from '@apollo/react-hooks';
import {
  AttachmentMajorMonotone,
  MentionMajorMonotone,
  CirclePlusMinor,
  DuplicateMinor,
  PrintMajorMonotone,
  CancelSmallMinor,
  TimelineAttachmentMajorMonotone,
  TickMinor,
  ClipboardMinor,
} from '@shopify/polaris-icons';
import { MentionsInput, Mention } from 'react-mentions';
import Loader from '../components/SkeletonLoader';
import Spinner from 'react-loader-spinner';
import moment from 'moment';
import { noJobsFoundImgUrl } from '../../utils/noJobsFoundImgUrl';
import { validateEmail } from '../../utils/normalizer';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import { Editor } from '@tinymce/tinymce-react';
import Footer from './Footer';
import { GET_CANDIDATE, GET_CURRENT_SUBSCRIPTION, GET_TAGS, GET_EMAIL_TEMPLATES } from '../apollo/queries';
import {
  UPDATE_CANDIDATE_STATUS,
  UPDATE_CANDIDATE_ADDRESS,
  CREATE_CANDIDATE_SCORE,
  UPDATE_CANDIDATE_SCORE,
  UPDATE_CANDIDATE_TAGS,
  CREATE_CANDIDATE_COMMENT,
  DELETE_CANDIDATE_COMMENT,
  CREATE_REFERENCE,
  SEND_CANDIDATE_EMAIL,
} from '../apollo/mutation';

export const overallRatingButtons = [
  { id: '1', name: 'Very Poor' },
  { id: '2', name: 'Poor' },
  { id: '3', name: 'Ok' },
  { id: '4', name: 'Good' },
  { id: '5', name: 'Excellent' },
];

export const statusBadgeColors = {
  active: 'success',
  inactive: 'default',
  hired: 'attention',
  new_candidate: 'info',
};

export const getOverallRatingKey = (rating) => {
  if (!!rating) {
    let parts = `${rating}`.split(' ');
    parts = parts.map((el) => `${el}`.toLowerCase());
    return parts.join('_');
  }
  return rating;
};

const regex = /@\[([\w\d\s]*)\]\(\d*\)/g;

const isNilOrEmpty = (value) => isNil(value) || isEmpty(value);

const Candidate = (props) => {
  const initialCandidateId = path(['match', 'params', 'id'])(props);
  const push = path(['history', 'push'])(props);
  const [currentCandidateId, setCurrentCandidateId] = useState(initialCandidateId);
  const node = document.getElementsByClassName('shop_container');
  const dataProps = JSON.parse(node[0].getAttribute('data-react-props'));
  const shopifyToken = path(['shop', 'shopify_token'])(dataProps);

  const [file, setFile] = useState();
  const handleDropZoneDrop = (_dropFiles, acceptedFiles, _rejectedFiles) => {
    setFile(acceptedFiles[0]);
    setDropzone(false);
  };

  const fileUpload = !file && <DropZone.FileUpload actionTitle='Add attachment' actionHint='or drop an file to upload' />;

  const componentRef = useRef();
  const componentQuestionnaireRef = useRef();
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });
  const handlePrintQuestionnaires = useReactToPrint({
    content: () => componentQuestionnaireRef.current,
  });
  const [isModalOpen, setIsModalOpen] = useState('');
  const [isUploadingData, setIsUploadingData] = useState(false);
  const [mentionsBox, setMentionsBox] = useState(false);
  const [referenceModal, setReferenceModal] = useState(false);
  const [toastStatus, setToastStatus] = useState({ status: false, message: '' });
  const [form, setFormData] = useState({ thoughts: '', overallRating: '' });
  const [referenceForm, setReferenceForm] = useState({});
  const [emailForm, setEmailForm] = useState({});
  const [tagOptions, setTagOptions] = useState([]);
  const [allTagOptions, setAllTagOptions] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [filterMentions, setFilterMentions] = useState('');
  const [isTagModalOpen, setIsTagModalOpen] = useState('');
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');
  const [dropzone, setDropzone] = useState(false);
  const [emailModal, setEmailModal] = useState(false);
  const [addressModal, setAddressModal] = useState(false);
  const [addressForm, setAddressForm] = useState({});
  const [emailTemplate, setEmailTemplate] = useState();
  const [messageOpen, setMessageOpen] = useState();

  const [selectedTab, setSelectedTab] = useState(0);
  const handleTabChange = useCallback((selectedTabIndex) => setSelectedTab(selectedTabIndex), []);
  const toggleDropZone = () => setDropzone(!dropzone);

  const toggleTagModal = () => setIsTagModalOpen(!isTagModalOpen);
  const toggleMentionsBox = () => setMentionsBox(!mentionsBox);
  const toggleReferenceModal = () => setReferenceModal(!referenceModal);
  const [updateCandidateStatus] = useMutation(UPDATE_CANDIDATE_STATUS);
  const [updateCandidateAddress] = useMutation(UPDATE_CANDIDATE_ADDRESS);
  const [createCandidateScore] = useMutation(CREATE_CANDIDATE_SCORE);
  const [updateCandidateScore] = useMutation(UPDATE_CANDIDATE_SCORE);
  const [updateCandidateTags] = useMutation(UPDATE_CANDIDATE_TAGS);
  const [createCandidateComment] = useMutation(CREATE_CANDIDATE_COMMENT);
  const [createReference] = useMutation(CREATE_REFERENCE);
  const [deleteCandidateComment] = useMutation(DELETE_CANDIDATE_COMMENT);
  const [sendCandidateEmail] = useMutation(SEND_CANDIDATE_EMAIL);

  const getCandidatePayload = { variables: { shopifyToken, candidateId: currentCandidateId } };

  const { data: candidate, loading: isFetching, refetch } = useQuery(GET_CANDIDATE, getCandidatePayload);
  const { data: subscriptionData } = useQuery(GET_CURRENT_SUBSCRIPTION, { variables: { shopifyToken } });
  const { data: tagsData, refetch: tagsRefetch } = useQuery(GET_TAGS, { variables: { shopifyToken } });
  const { data: templatesData } = useQuery(GET_EMAIL_TEMPLATES, { variables: { shopifyToken } });

  const { emailTemplates = [] } = { ...templatesData };
  const externalTemplates = (emailTemplates || []).filter(
    ({ group, status }) => (group === 'external' || group === 'user') && status === 'active'
  );
  const shop = propOr({}, 'shop')(subscriptionData);
  const { currentSubscription = {}, teamMembers = [] } = { ...shop };
  const isSubscribedUser = !isNilOrEmpty(currentSubscription);

  const isAllowedUser =
    isSubscribedUser &&
    (currentSubscription.subscriptionType === 'Premium' ||
      currentSubscription.subscriptionType === 'Growth' ||
      currentSubscription.subscriptionType === 'Business');

  const dispositionCodes = propOr([], 'dispositionCodes')(shop);

  const tags = propOr([], 'tags')(tagsData);

  useEffect(() => {
    if (!isEmpty(tags) && isEmpty(allTagOptions)) {
      const options = tags.map(({ name }) => ({
        label: name,
        value: name,
      }));
      setTagOptions(options);
      setAllTagOptions(options);
    }
  }, [tags]);

  // Update currentCandidateId when URL params change
  useEffect(() => {
    setCurrentCandidateId(initialCandidateId);
  }, [initialCandidateId]);

  // Reset form state when candidate changes
  useEffect(() => {
    setFormData({ thoughts: '', overallRating: '' });
    setReason('');
    setComment('');
    setSelectedTab(0);
    setFile();
    setDropzone(false);
    setEmailModal(false);
    setAddressModal(false);
    setReferenceModal(false);
    setIsModalOpen('');
  }, [currentCandidateId]);

  const job = pathOr({}, ['candidate', 'job'])(candidate);
  const email = path(['candidate', 'email'])(candidate);

  const tabs = [
    {
      id: 'timeline',
      content: 'Timeline',
      accessibilityLabel: 'Timeline',
      panelID: 'timeline',
    },
    {
      id: 'application-form',
      content: 'Application form',
      accessibilityLabel: 'Application form',
      panelID: 'application-form',
    },
    {
      id: 'resume',
      content: 'Resume',
      panelID: 'resume',
    },
    (job?.questionnaires || []).length > 0
      ? {
          id: 'questionnaire',
          content: 'Questionnaire',
          panelID: 'questionnaire',
        }
      : null,
  ].filter((e) => !!e);

  const candidateQuestionnaire = path(['candidate', 'candidateQuestionnaire'])(candidate);
  const candidateReferences = pathOr([], ['candidate', 'references'])(candidate);

  useEffect(() => {
    const candidateScore = path(['candidate', 'candidateScore'])(candidate);

    if (!isNil(candidateScore)) {
      const overallRating = propOr('', 'overallRating')(candidateScore);

      setFormData({
        thoughts: prop('thoughts')(candidateScore),
        overallRating: overallRating ? `${overallRating[0].toUpperCase()}${overallRating.substring(1)}` : '',
      });
    }
  }, [candidate]);

  const candidateName = path(['candidate', 'name'])(candidate);
  const candidateLastName = path(['candidate', 'lastName'])(candidate);
  const jobTitle = path(['candidate', 'jobTitle'])(candidate);
  const phone = path(['candidate', 'phone'])(candidate);
  const address = path(['candidate', 'address'])(candidate);
  const { address: addressCandidate = '', city = '', state = '', unit = '', zipcode = '' } = { ...address };
  const status = path(['candidate', 'status'])(candidate);
  const candidateTags = path(['candidate', 'tags'])(candidate);
  const candidateLogs = path(['candidate', 'logs'])(candidate);
  const candidateComments = path(['candidate', 'comments'])(candidate);
  const resume = path(['candidate', 'resume'])(candidate);
  const createdAt = path(['candidate', 'createdAt'])(candidate);
  const coverLetter = path(['candidate', 'coverLetter'])(candidate);
  const socialLinks = path(['candidate', 'socialLinks'])(candidate);
  const dispositionCode = path(['candidate', 'dispositionCode'])(candidate);
  const { linkedin_url = '', github_url = '', portfolio_url = '', personal_website = '' } = { ...socialLinks };
  const workExperiences = path(['candidate', 'workExperiences'])(candidate);
  const visitorData = path(['candidate', 'visitorData'])(candidate);
  const { device = '' } = { ...visitorData };
  const source = path(['candidate', 'source'])(candidate);
  const isCandidateActive = equals(status, 'active');
  const hasCandidateScore = compose(not, isNil, path(['candidate', 'candidateScore']))(candidate);

  const previousCandidate = path(['candidate', 'previous'])(candidate);
  const nextCandidate = path(['candidate', 'next'])(candidate);

  const jobCategory = prop('category')(job);
  const jobDepartment = prop('department')(job);
  const jobCity = prop('city')(job);
  const jobCountry = prop('country')(job);
  const jobTeamMembers = prop('teamMembers')(job) || [];
  const jobPostalCode = prop('postalCode')(job);
  const jobState = prop('state')(job);
  const jobStreetAddress = prop('streetAddress')(job);
  const jobId = prop('id')(job);

  const { headingText: departmentName } = { ...jobDepartment };

  const toggleAddressModal = () => {
    setAddressModal(!addressModal);
    setAddressForm({
      address: addressCandidate,
      unit,
      city,
      state,
      zipcode,
    });
  };

  const questionnaireLogs = [];
  if (!isNilOrEmpty(candidateQuestionnaire)) {
    if (!!candidateQuestionnaire?.token) {
      questionnaireLogs.push({
        created_at: candidateQuestionnaire?.createdAt,
        parameters: { ...candidateQuestionnaire },
        key: 'candidate.candidate_questionnaire_sent',
      });
      if (!isNilOrEmpty(candidateQuestionnaire?.score)) {
        questionnaireLogs.push({
          created_at: candidateQuestionnaire?.answeredAt,
          parameters: { ...candidateQuestionnaire },
          key: 'candidate.candidate_questionnaire_score',
        });
      }
    }
  }

  const candidateTimeline = [
    ...(candidateLogs || []),
    ...questionnaireLogs,
    ...(candidateComments || []).map((comment) => {
      const { createdAt, user, content } = { ...comment };
      return {
        created_at: createdAt,
        parameters: {
          ...comment,
        },
        key: 'candidate.candidate_commented',
      };
    }),
  ].sort((a, b) => moment(b.created_at).format('YYYYMMDDHHmm') - moment(a.created_at).format('YYYYMMDDHHmm'));

  const onGoToJobPage = () => push(`/job-candidates/${jobId}`);

  const navigateToNextCandidate = () => {
    if (nextCandidate && nextCandidate !== currentCandidateId && !isFetching) {
      setCurrentCandidateId(nextCandidate);
    }
  };

  const navigateToPreviousCandidate = () => {
    if (previousCandidate && previousCandidate !== currentCandidateId && !isFetching) {
      setCurrentCandidateId(previousCandidate);
    }
  };

  const toggleIsModalOpen = (value = '') => {
    setIsModalOpen(value);
    if (!value) {
      setReason('');
    } else if (value === 'inactive') {
      // Reset reason to empty string to show placeholder
      setReason('');
    }
  };
  const onDisableOverallRatingButton = (name) => equals(form.overallRating, name);
  const onSetOverallRating = (overallRating) => () => setFormData({ ...form, overallRating });

  useEffect(() => {
    if (isEmpty(selectedTags)) {
      setSelectedTags((candidateTags || []).map(({ name }) => name));
    }
  }, [candidateTags]);

  const updateCandidateStatusSuccess = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'The candidate status was updated successfully.' });
    toggleIsModalOpen();
    refetch();
  };

  const updateCandidateAddressSuccess = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'The candidate address was updated successfully.' });
    toggleAddressModal();
    refetch();
  };

  const createReferenceSuccess = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'The candidate reference was created successfully.' });
    toggleReferenceModal();
    refetch();
  };

  const onFailure = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'There is an error with the server' });
  };

  const onUpdateCandidateStatus = () => {
    setIsUploadingData(true);

    updateCandidateStatus({
      variables: { candidateId: currentCandidateId, shopifyToken, status: isModalOpen, reasonId: isModalOpen === 'inactive' ? (reason || null) : null },
    })
      .then(updateCandidateStatusSuccess)
      .catch(onFailure);
  };

  const onUpdateCandidateAddress = () => {
    setIsUploadingData(true);

    updateCandidateAddress({
      variables: { candidateId: currentCandidateId, shopifyToken, ...addressForm },
    })
      .then(updateCandidateAddressSuccess)
      .catch(onFailure);
  };

  const sendCandidateEmailSuccess = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'The email was sent successfully.' });
    toggleEmailModal();
    refetch();
  };

  const onSendEmail = () => {
    setIsUploadingData(true);

    sendCandidateEmail({
      variables: { shopifyToken, candidateId: currentCandidateId, ...emailForm },
    })
      .then(sendCandidateEmailSuccess)
      .catch(onFailure);
  };

  const onCreateReference = () => {
    setIsUploadingData(true);

    createReference({
      variables: { candidateId: currentCandidateId, shopifyToken, ...referenceForm },
    })
      .then(createReferenceSuccess)
      .catch(onFailure);
  };

  const createCandidateCommentSuccess = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'The comment was stored successfully.' });
    setComment('');
    setFile();
    setDropzone(false);
    refetch();
  };

  const deleteCandidateCommentSuccess = () => {
    setIsUploadingData(false);
    setToastStatus({ status: true, message: 'The comment was deleted successfully.' });
    setComment('');
    refetch();
  };

  const onCreateCandidateComment = () => {
    setIsUploadingData(true);
    const mentions = comment.match(regex) || [];
    const ids = mentions.map((m) =>
      m
        .match(/\(\d*\)/)
        .join('')
        .replaceAll('(', '')
        .replaceAll(')', '')
    );
    createCandidateComment({
      variables: { candidateId: currentCandidateId, shopifyToken, content: comment, user: 'Hiring team', mentions: ids, attachment: file },
    })
      .then(createCandidateCommentSuccess)
      .catch(onFailure);
  };

  const onAddMention = (member) => {
    const { firstName, lastName, id } = { ...member };
    setComment(`${comment}@[${firstName} ${lastName}](${id})`);
    toggleMentionsBox();
  };

  const onDeleteCandidateComment = (commentId) => {
    setIsUploadingData(true);
    deleteCandidateComment({
      variables: { commentId, shopifyToken },
    })
      .then(deleteCandidateCommentSuccess)
      .catch(onFailure);
  };

  const updateText = (value) => {
    setInputValue(value);

    if (value === '') {
      setTagOptions(allTagOptions);
      return;
    }

    const filterRegex = new RegExp(value, 'i');
    const resultOptions = allTagOptions.filter((option) => option.label.match(filterRegex));
    setTagOptions(resultOptions);
  };

  const handleThoughtsChanges = (thoughts) => {
    setFormData({ ...form, thoughts });
  };

  const createCandidateScoreSuccess = () => {
    setIsUploadingData(false);
    refetch();
    setToastStatus({ status: true, message: 'Your review was saved successfully.' });
  };

  const updateCandidateScoreSuccess = () => {
    setIsUploadingData(false);
    refetch();
    setToastStatus({ status: true, message: 'Your review was updated successfully.' });
  };

  const statusName = (status) => {
    if ('new_candidate' === status) return 'New';
    if ('inactive' === status) return 'Disqualified';
    return `${status}`.charAt(0).toUpperCase() + `${status}`.slice(1);
  };

  const onSave = () => {
    setIsUploadingData(true);

    createCandidateScore({
      variables: { candidateId: currentCandidateId, shopifyToken, ...form, overallRating: getOverallRatingKey(form.overallRating) },
    })
      .then(createCandidateScoreSuccess)
      .catch(onFailure);
  };

  const onUpdate = () => {
    setIsUploadingData(true);

    updateCandidateScore({
      variables: { candidateId: currentCandidateId, shopifyToken, ...form, overallRating: getOverallRatingKey(form.overallRating) },
    })
      .then(updateCandidateScoreSuccess)
      .catch(onFailure);
  };

  const handleUpdateCandidateTags = () => {
    setIsTagModalOpen(false);
    updateCandidateTags({
      variables: { candidateId: currentCandidateId, shopifyToken, tags: selectedTags },
    })
      .then(() => {
        setToastStatus({ status: true, message: 'Candidate tag list was updated successfully.' });
        refetch();
        tagsRefetch();
      })
      .catch(onFailure);
  };

  if (isFetching) {
    return (
      <AppProvider>
        <Loader secondary={true} />
      </AppProvider>
    );
  }

  if (isNil(candidate) || isEmpty(candidate)) push('/candidate-list');

  const formattedJobCategory = `${jobCategory}`
    .split(' ')
    .map((word) => {
      if (word === 'and') return '&';
      return `${word[0].toUpperCase()}${word.slice(1)}`;
    })
    .join(' ');

  const removeTag = (tag) => () => {
    const options = [...selectedTags];
    options.splice(options.indexOf(tag), 1);
    setSelectedTags(options);
  };

  const handleNewTag = () => {
    const newOption = { label: inputValue, value: inputValue };
    setSelectedTags([...selectedTags, inputValue]);
    setAllTagOptions([...allTagOptions, newOption]);
    setTagOptions([...allTagOptions, newOption]);
    setInputValue('');
  };

  const tagsMarkup = selectedTags.map((option) => {
    return (
      <Tag key={`option${option}`} onRemove={removeTag(option)}>
        {option}
      </Tag>
    );
  });

  const hasTagsChanges = () => {
    const a = selectedTags.sort();
    const b = (candidateTags || []).map(({ name }) => name).sort();
    return !equals(a, b);
  };

  const setOptionLabel = (index) => {
    switch (index) {
      case 0:
        return 'A';
      case 1:
        return 'B';
      case 2:
        return 'C';
      case 3:
        return 'D';
      case 4:
        return 'E';
      case 5:
        return 'F';
      case 6:
        return 'G';
      case 7:
        return 'H';
      default:
        return '';
    }
  };

  const setOptionsQuestionsStyles = ({ type, value, answer }) => {
    if (type === 'checkboxes') {
      return { fontWeight: answer.includes(value) ? '700' : '' };
    }
    return { fontWeight: value === answer ? '700' : '' };
  };

  const toggleEmailModal = () => {
    setEmailForm({ to: email });
    setEmailTemplate();
    setEmailModal(!emailModal);
  };

  const hasMinAddress = !isEmpty(addressCandidate);

  const onChangeEmailTemplate = (template) => {
    setEmailTemplate(template);
    const selected = externalTemplates.find(({ id }) => id === template);
    if (!!selected) {
      const { body: message, subject } = { ...selected };
      setEmailForm({ ...emailForm, subject, message });
    }
  };

  return (
    <AppProvider>
      <Frame>
        <Page
        breadcrumbs={[{ content: 'All candidates', onAction: () => { push('/candidate-list/all'); } }]}
          title={`${candidateName}${!!candidateLastName ? ` ${candidateLastName}` : ''}`}
          pagination={{
            hasPrevious: !isFetching && !!previousCandidate && previousCandidate !== currentCandidateId,
            hasNext: !isFetching && !!nextCandidate && nextCandidate !== currentCandidateId,
            onPrevious: navigateToPreviousCandidate,
            onNext: navigateToNextCandidate,
            nextTooltip: 'Next',
            previousTooltip: 'Previous',
          }}
          primaryAction={
            <Button
              primary
              connectedDisclosure={{
                accessibilityLabel: 'Candidate status',
                actions: [
                  {
                    content: 'Active',
                    onAction: () => status !== 'active' && toggleIsModalOpen('active'),
                    active: status === 'active',
                  },
                  {
                    content: 'Hired',
                    onAction: () => status !== 'hired' && toggleIsModalOpen('hired'),
                    active: status === 'hired',
                  },
                  {
                    content: 'Disqualify',
                    onAction: () => status !== 'inactive' && toggleIsModalOpen('inactive'),
                    active: status === 'inactive',
                  },
                ],
              }}>
              {statusName(status)}
            </Button>
          }>
          <Form>
            <Layout>
              <Layout.Section>
                <Card>
                  <Card.Section>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <div>
                          <h2 className='Polaris-Heading' style={{ display: 'inline-block', marginRight: '10px' }}>
                            {candidateName} {candidateLastName}
                          </h2>
                          {status !== 'hired' ? (
                            <Badge status={statusBadgeColors[status]}>
                              <span className='capitalize'>{status === 'new_candidate' ? 'New' : status}</span>
                            </Badge>
                          ) : (
                            <span
                              style={{
                                backgroundColor: '#5EEEEB',
                                border: '0.2rem solid #fff',
                                color: '#454f5b',
                                padding: '.2rem .8rem',
                                borderRadius: '25px',
                                textAlign: 'center',
                                fontSize: '1.3rem',
                                lineHeight: '2rem',
                              }}>
                              Hired
                            </span>
                          )}
                        </div>
                        <TextStyle variation='subdued'>Applied {moment(createdAt).format('MMMM D, YYYY')}</TextStyle>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Subheading>{jobTitle}</Subheading>
                        <Link url={resume} external={true} removeUnderline>
                          View resume
                        </Link>
                      </div>
                    </div>
                  </Card.Section>
                  <Card.Section>
                    <TextField
                      label='Thoughts on this candidate?'
                      value={prop('thoughts')(form)}
                      onChange={handleThoughtsChanges}
                      multiline={5}
                    />
                    <br />
                    <Subheading>Rating</Subheading>
                    <br />
                    <Stack>
                      <ButtonGroup segmented>
                        {overallRatingButtons.map(({ id, name: buttonName }) => (
                          <Button
                            onClick={onSetOverallRating(buttonName)}
                            disabled={onDisableOverallRatingButton(buttonName)}
                            key={id}>
                            {buttonName}
                          </Button>
                        ))}
                      </ButtonGroup>
                      <Stack.Item fill>
                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                          <Button
                            primary
                            disabled={isEmpty(form.overallRating) && isEmpty(form.thoughts)}
                            onClick={hasCandidateScore ? onUpdate : onSave}>
                            {hasCandidateScore ? 'Update' : 'Save'}
                          </Button>
                        </div>
                      </Stack.Item>
                    </Stack>
                  </Card.Section>
                </Card>
                <Card>
                  <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange}>
                    {selectedTab === 0 && (
                      <>
                        <div className={'timeline-content'}>
                          <div style={{ padding: '20px', paddingBottom: '0px', paddingTop: '20px' }}>
                            <div className={'comment-box'}>
                              <Stack vertical>
                                <Stack fullWidth spacing={'extraTight'}>
                                  <Stack.Item fill>
                                    <MentionsInput
                                      value={comment}
                                      onChange={(e) => setComment(e.target.value)}
                                      singleLine={true}
                                      placeholder={'Leave a comment...'}
                                      className={'mentions'}>
                                      <Mention
                                        trigger='@'
                                        className={'mentions__mention'}
                                        data={jobTeamMembers.map((member) => {
                                          const { firstName, lastName } = { ...member };
                                          return {
                                            ...member,
                                            display: `${firstName} ${lastName}`,
                                          };
                                        })}
                                      />
                                    </MentionsInput>
                                  </Stack.Item>
                                  <ButtonGroup segmented>
                                    <Popover
                                      active={mentionsBox}
                                      activator={<Button onClick={toggleMentionsBox} icon={MentionMajorMonotone}></Button>}
                                      onClose={toggleMentionsBox}>
                                      <Popover.Pane fixed>
                                        <Popover.Section>
                                          <TextField value={filterMentions} onChange={setFilterMentions} placeholder={'Search'} />
                                        </Popover.Section>
                                      </Popover.Pane>
                                      <Popover.Pane>
                                        <ActionList
                                          items={jobTeamMembers
                                            .filter((member) => {
                                              const { firstName, lastName } = { ...member };
                                              return (
                                                !filterMentions ||
                                                `${firstName} ${lastName}`
                                                  .toLowerCase()
                                                  .indexOf(`${filterMentions}`.toLowerCase()) !== -1
                                              );
                                            })
                                            .map((member) => {
                                              const { firstName, lastName } = { ...member };
                                              return {
                                                content: `${firstName} ${lastName}`,
                                                onAction: () => onAddMention(member),
                                              };
                                            })}
                                        />
                                      </Popover.Pane>
                                    </Popover>
                                    <Button onClick={toggleDropZone} disabled={!!file} icon={AttachmentMajorMonotone}></Button>
                                    <Button primary onClick={onCreateCandidateComment} disabled={!comment}>
                                      Post
                                    </Button>
                                  </ButtonGroup>
                                </Stack>
                                {dropzone && !file && (
                                  <DropZone allowMultiple={false} onDrop={handleDropZoneDrop}>
                                    {fileUpload}
                                  </DropZone>
                                )}
                                {!!file && (
                                  <>
                                    <Stack>
                                      <Thumbnail
                                        size='small'
                                        alt={file.name}
                                        source={
                                          'https://cdn.shopify.com/s/files/1/0757/9955/files/New_Post.png?12678548500147524304'
                                        }
                                      />
                                      <div style={{ textAlign: 'left' }}>
                                        {file.name} <Caption>{file.size} bytes</Caption>
                                      </div>
                                      <Button monochrome plain icon={CancelSmallMinor} onClick={() => setFile()}></Button>
                                    </Stack>
                                    <br />
                                  </>
                                )}
                              </Stack>
                            </div>
                          </div>
                          <ul className={'timeline'}>
                            {(candidateTimeline || [])
                              .filter((log) => {
                                const { parameters = {}, key = '' } = { ...log };
                                return (
                                  Object.keys(parameters).length > 0 &&
                                  [
                                    'candidate.candidate_status_changed',
                                    'candidate.candidate_scored',
                                    'candidate.candidate_questionnaire_sent',
                                    'candidate.candidate_questionnaire_score',
                                    'candidate.candidate_tagged',
                                    'candidate.candidate_commented',
                                    'candidate.candidate_email_sent',
                                    'candidate_questionnaire_reminder',
                                  ].indexOf(key) !== -1
                                );
                              })
                              .map((log, index) => {
                                const { created_at: logDate, parameters = {}, key = '', id: logId } = { ...log };
                                let logMessage = 'Candidate was updated';
                                let extraMessage = '';
                                const {
                                  status = [],
                                  disposition_code_id = [],
                                  overall_rating = [],
                                  thoughts = [],
                                  tags = [],
                                  attachment = '',
                                  user = '',
                                  content = '',
                                  score = 0,
                                  id: commentId = '',
                                  to = '',
                                  from = '',
                                  subject = '',
                                  message = '',
                                } = {
                                  ...parameters,
                                };
                                switch (key) {
                                  case 'candidate.candidate_status_changed':
                                    const dispositionCode = dispositionCodes.find(
                                      ({ id }) => `${id}` === `${disposition_code_id[1]}`
                                    );
                                    logMessage =
                                      !!status && !!status[0] && !!status[1]
                                        ? `${candidateName} was moved from ${statusName(status[0])} to ${statusName(status[1])}`
                                        : `${candidateName} was disqualified for the ${jobTitle} position because of ${
                                            dispositionCode ? `${dispositionCode.name}`.toLowerCase() : 'not specified reason'
                                          }`;
                                    if (!!status && !!status[1] && status[1] === 'hired') {
                                      logMessage = `${candidateName} was Hired for the ${jobTitle} position.`;
                                    }
                                    break;
                                  case 'candidate.candidate_scored':
                                    logMessage = `${candidateName} was rated as ${
                                      !!overall_rating && !!overall_rating[1] ? statusName(overall_rating[1]) : form.overallRating
                                    }`;
                                    extraMessage = !!thoughts && !!thoughts[1] ? thoughts[1] : '';
                                    break;
                                  case 'candidate.candidate_email_sent':
                                    const author = teamMembers.find(({ email }) => email === from);
                                    const authorLabel = !!author
                                      ? `${author?.firstName} ${author?.lastName} <${from}>`
                                      : `${from}`;
                                    logMessage = `An email was sent from ${authorLabel} to this candidate (${to})`;
                                    break;
                                  case 'candidate.candidate_tagged':
                                    logMessage = isEmpty(tags)
                                      ? `All tags of ${candidateName} were removed`
                                      : `${candidateName} was tagged as ${tags.join(', ')}`;
                                    break;
                                  case 'candidate.candidate_questionnaire_sent':
                                    logMessage = `${jobTitle} questionnaire link was sent to ${candidateName}`;
                                    break;
                                  case 'candidate.candidate_questionnaire_score':
                                    logMessage = `${candidateName} scored ${score}% on the ${jobTitle} questionnaire`;
                                    break;
                                  case 'candidate.candidate_questionnaire_reminder':
                                    logMessage = `A reminder email was sent to ${candidateName} to complete the ${jobTitle} questionnaire`;
                                    break;
                                  case 'candidate.candidate_commented':
                                    logMessage = `${user} commented:`;
                                    const mentions = content.match(regex) || [];
                                    const scapes = mentions.map((m) =>
                                      m
                                        .replaceAll(m.match(/\(\d*\)/).join(''), '')
                                        .replaceAll('[', '')
                                        .replaceAll(']', '')
                                    );
                                    extraMessage = content;
                                    mentions.forEach((m, i) => {
                                      extraMessage = extraMessage.replaceAll(m, scapes[i]);
                                    });
                                    break;
                                  default:
                                    logMessage = 'Candidate was updated';
                                }
                                return (
                                  <li className={'event'} key={index}>
                                    {key === 'candidate.candidate_commented' && (
                                      <div className={'delete'} onClick={() => onDeleteCandidateComment(commentId)}>
                                        <Icon source={CancelSmallMinor} />
                                      </div>
                                    )}
                                    <TextStyle>{logMessage}</TextStyle>
                                    <br />
                                    <i>{extraMessage}</i>
                                    {!!subject && !!message && (
                                      <>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                          <Button plain onClick={() => setMessageOpen(!!messageOpen ? undefined : logId)}>
                                            {`${messageOpen === logId ? 'Hide' : 'Show'} message`}
                                          </Button>
                                        </div>
                                        <Collapsible
                                          open={messageOpen === logId}
                                          id='basic-collapsible'
                                          transition={{ duration: '500ms', timingFunction: 'ease-in-out' }}
                                          expandOnPrint>
                                          <>
                                            <br />
                                            <Card>
                                              <Card.Section>
                                                <Stack>
                                                  <TextStyle variation={'strong'}>{subject}</TextStyle>
                                                  <div dangerouslySetInnerHTML={{ __html: message }}></div>
                                                </Stack>
                                              </Card.Section>
                                            </Card>
                                          </>
                                        </Collapsible>
                                      </>
                                    )}

                                    {attachment && (
                                      <div style={{ marginTop: '10px' }}>
                                        <Button
                                          size={'medium'}
                                          plain
                                          url={attachment}
                                          icon={TimelineAttachmentMajorMonotone}
                                          external>
                                          {`${attachment}`.substring(`${attachment}`.lastIndexOf('/') + 1)}
                                        </Button>
                                      </div>
                                    )}
                                    <br />
                                    <div className={'date'}>
                                      <TextStyle variation='subdued'>
                                        {moment.duration(moment(logDate).diff(moment())).humanize(true)}
                                      </TextStyle>
                                    </div>
                                  </li>
                                );
                              })}
                            <li className={'event'}>
                              <TextStyle>
                                {candidateName} applied to the{' '}
                                <Link onClick={onGoToJobPage} removeUnderline>
                                  {jobTitle}
                                </Link>{' '}
                                job on {device || 'unknown device'}
                                {!!source ? ` and was referred by ${source.toLowerCase()}` : ''}
                              </TextStyle>
                              <br />
                              <div className={'date'}>
                                <TextStyle variation='subdued'>
                                  {moment.duration(moment(createdAt).diff(moment())).humanize(true)}
                                </TextStyle>
                              </div>
                            </li>
                          </ul>
                        </div>
                      </>
                    )}
                    {selectedTab === 1 && (
                      <>
                        <div style={{ float: 'right', margin: '20px' }}>
                          <Tooltip active={false} content="Print candidate's application form">
                            <Button plain monochrome onClick={handlePrint} icon={PrintMajorMonotone} />
                          </Tooltip>
                        </div>
                        <div ref={componentRef}>
                          <Card.Section>
                            <TextContainer>
                              <Subheading>Candidate</Subheading>
                              <p>
                                <strong>Name:</strong> {`${candidateName}${!!candidateLastName ? ` ${candidateLastName}` : ''}`}
                              </p>
                              <p>
                                <strong>Email:</strong> {email}
                              </p>
                              <p>
                                <strong>Phone:</strong> {phone}
                              </p>
                            </TextContainer>
                          </Card.Section>
                          {!(
                            isEmpty(linkedin_url) &&
                            isEmpty(github_url) &&
                            isEmpty(portfolio_url) &&
                            isEmpty(personal_website)
                          ) && (
                            <Card.Section>
                              <TextContainer>
                                <Subheading>Social Links</Subheading>
                                {linkedin_url && (
                                  <p>
                                    <strong>Linkedin:</strong> {linkedin_url}
                                  </p>
                                )}
                                {github_url && (
                                  <p>
                                    <strong>Github:</strong> {github_url}
                                  </p>
                                )}
                                {portfolio_url && (
                                  <p>
                                    <strong>Portfolio:</strong> {portfolio_url}
                                  </p>
                                )}
                                {personal_website && (
                                  <p>
                                    <strong>Personal Website:</strong> {personal_website}
                                  </p>
                                )}
                              </TextContainer>
                            </Card.Section>
                          )}
                          {!(
                            isEmpty(addressCandidate) &&
                            isEmpty(city) &&
                            isEmpty(state) &&
                            isEmpty(unit) &&
                            isEmpty(zipcode)
                          ) && (
                            <Card.Section>
                              <TextContainer>
                                <Subheading>Address</Subheading>
                                {addressCandidate && (
                                  <p>
                                    <strong>Address:</strong> {addressCandidate}
                                  </p>
                                )}
                                {unit && (
                                  <p>
                                    <strong>Unit:</strong> {unit}
                                  </p>
                                )}
                                {city && (
                                  <p>
                                    <strong>City:</strong> {city}
                                  </p>
                                )}
                                {state && (
                                  <p>
                                    <strong>State:</strong> {state}
                                  </p>
                                )}
                                {zipcode && (
                                  <p>
                                    <strong>Zipcode:</strong> {zipcode}
                                  </p>
                                )}
                              </TextContainer>
                            </Card.Section>
                          )}
                          {!isEmpty(workExperiences) && (
                            <Card.Section>
                              <TextContainer>
                                <Subheading>Work Experiences</Subheading>
                                {workExperiences.map((workExperience) => {
                                  const {
                                    jobTitle = '',
                                    companyName = '',
                                    startDate = '',
                                    endDate = '',
                                    id = '',
                                    current = false,
                                  } = {
                                    ...workExperience,
                                  };
                                  return (
                                    <Stack key={id}>
                                      <Stack.Item fill>
                                        <p>
                                          <strong>{jobTitle}</strong>
                                        </p>
                                        <p>{companyName}</p>
                                      </Stack.Item>
                                      <p>
                                        {moment(startDate, 'MM/YYYY').format('MMM, YYYY')} -{' '}
                                        {current ? 'Present' : moment(endDate, 'MM/YYYY').format('MMM, YYYY')}
                                      </p>
                                    </Stack>
                                  );
                                })}
                              </TextContainer>
                            </Card.Section>
                          )}
                          {!!candidate?.candidate?.customQuestions?.length && (
                            <Card.Section>
                              {candidate.candidate.customQuestions.map(
                                ({ question, answer, options, type, termsAndConditions }, index) => (
                                  <div key={index} style={{ marginBottom: '30px' }}>
                                    <Subheading>
                                      {termsAndConditions?.checkboxLabelText ? termsAndConditions.checkboxLabelText : question}
                                    </Subheading>

                                    {type !== 'checkboxes' && type !== 'terms-and-conditions' && (
                                      <p style={{ margin: '0' }}>{answer}</p>
                                    )}

                                    {!!options?.length && type !== 'terms-and-conditions' && (
                                      <div style={{ padding: '10px 20px 0' }}>
                                        {options.map(({ id, value }, index) => (
                                          <div key={id} style={setOptionsQuestionsStyles({ answer, value, type })}>
                                            <span>{setOptionLabel(index)}) </span>
                                            <span>{value}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}

                                    {type == 'terms-and-conditions' && (
                                      <p style={{ margin: '0' }}>{answer == '1' ? 'true' : 'false'}</p>
                                    )}
                                  </div>
                                )
                              )}
                            </Card.Section>
                          )}
                          {!!resume && (
                            <Card.Section title={'Resume'}>
                              <Link url={resume} external={true} removeUnderline>
                                View resume
                              </Link>
                            </Card.Section>
                          )}
                          <Card.Section>
                            <TextContainer>
                              <Subheading>Cover Letter</Subheading>
                              <p>{coverLetter || `${candidateName} did not submit a cover letter with their application.`}</p>
                            </TextContainer>
                          </Card.Section>
                        </div>
                      </>
                    )}
                    {selectedTab === 2 && (
                      <>
                        {!!resume ? (
                          <iframe src={resume} style={{ width: '100%', height: '60vh', borderWidth: '0' }} />
                        ) : (
                          <Card.Section>
                            <EmptyState heading='No resume found' image={noJobsFoundImgUrl}>
                              <p>There is no candidate's resume available</p>
                            </EmptyState>
                          </Card.Section>
                        )}
                      </>
                    )}
                    {selectedTab === 3 &&
                      (isAllowedUser ? (
                        <>
                          <div style={{float: 'right', margin: '20px'}}>
                            <Tooltip active={false} content="Print candidate's questionnaires">
                              <Button plain monochrome onClick={handlePrintQuestionnaires} icon={PrintMajorMonotone}/>
                            </Tooltip>
                          </div>
                          <div ref={componentQuestionnaireRef}>
                            <Card.Section>
                              <TextContainer>
                                <Subheading>Results</Subheading>
                                <p>
                                  <strong>Submission Date:</strong>{' '}
                                  {!!candidateQuestionnaire?.answeredAt
                                    ? moment(candidateQuestionnaire.answeredAt).format('MMMM D, YYYY')
                                    : 'Not submitted yet'}
                                </p>
                                <p
                                  style={
                                    !isNilOrEmpty(candidateQuestionnaire?.score) && candidateQuestionnaire.score === 0
                                      ? {color: '#d92b0c'}
                                      : {}
                                  }>
                                  <strong>Total score:</strong>{' '}
                                  {!isNilOrEmpty(candidateQuestionnaire?.score) ? `${candidateQuestionnaire.score}%` : 'N/A'}
                                </p>
                              </TextContainer>
                            </Card.Section>
                            {!!candidateQuestionnaire?.answers && (
                              <Card.Section>
                                <TextContainer>
                                  <Subheading>Answers</Subheading>
                                  {(candidateQuestionnaire?.answers || []).map((question, index) => {
                                    const {
                                      label = '',
                                      response = '',
                                      score = 0,
                                      video_index,
                                      response_value = '',
                                    } = {
                                      ...question,
                                    };
                                    return (
                                      <div style={{display: 'flex', flexDirection: 'row'}} key={index}>
                                        <div style={{width: '24px', height: '24px'}}>
                                          <Icon
                                            source={score !== 0 ? TickMinor : CancelSmallMinor}
                                            color={score !== 0 ? 'success' : 'critical'}
                                          />
                                        </div>
                                        <div style={{flexGrow: '1', marginLeft: '10px'}}>
                                          <strong>{label}:</strong>{' '}
                                          {response === 'video' ? (
                                            <Button plain url={candidateQuestionnaire.videos[video_index]} external>
                                              Download Video
                                            </Button>
                                          ) : (response === '' && JSON.stringify(response).indexOf('first_name') !== -1) ||
                                          response === 'references' ? (
                                            <ul>
                                              {Object.values(response_value).map((el) => (
                                                <li>
                                                  <p>{`${el?.first_name} ${el?.last_name}, ${el?.company}`}</p>
                                                  <TextStyle variation={'subdued'}>{el?.email}</TextStyle>
                                                </li>
                                              ))}
                                            </ul>
                                          ) : response ? (
                                            response
                                          ) : (
                                            response_value
                                          )}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </TextContainer>
                                <br/>
                              </Card.Section>
                            )}
                          </div>
                        </>
                      ) : (
                        <EmptyState heading='Upgrade your account plan'>
                          <Stack vertical>
                            <p>Questionnaires are a paid feature for Growth, Business and Premium account plans</p>
                            <Button primary onClick={() => changeRoute('/account-plan')}>
                              Upgrade account plan
                            </Button>
                          </Stack>
                        </EmptyState>
                      ))}
                  </Tabs>
                </Card>
              </Layout.Section>
              <Layout.Section secondary>
                <Card>
                  <Card.Section title={'Candidate overview'}>
                    <Stack vertical spacing={'extraTight'} fullWidth>
                      <Stack>
                        <Stack.Item fill>
                          <Button plain onClick={toggleEmailModal}>
                            {email}
                          </Button>
                        </Stack.Item>
                        <CopyToClipboard
                          text={email}
                          onCopy={() => {
                            setToastStatus({
                              status: true,
                              message: 'Copied to clipboard',
                            });
                          }}>
                          <Button monochrome plain icon={ClipboardMinor} size={'slim'}></Button>
                        </CopyToClipboard>
                      </Stack>
                      <Stack>
                        <Stack.Item fill>
                          <TextStyle>{phone}</TextStyle>
                        </Stack.Item>
                        <CopyToClipboard
                          text={phone}
                          onCopy={() => {
                            setToastStatus({
                              status: true,
                              message: 'Copied to clipboard',
                            });
                          }}>
                          <Button monochrome plain icon={ClipboardMinor} size={'slim'}></Button>
                        </CopyToClipboard>
                      </Stack>
                      {!!dispositionCode && <TextStyle variation='subdued'>{dispositionCode.name}</TextStyle>}
                    </Stack>
                  </Card.Section>
                  <Card.Section
                    title={'Address'}
                    actions={[{ content: hasMinAddress ? 'Edit' : 'Add', onAction: toggleAddressModal }]}>
                    {hasMinAddress ? (
                      <>
                        <p style={{ marginBottom: '0' }}>{isEmpty(unit) ? addressCandidate : `${addressCandidate}, ${unit}`}</p>
                        {!(isEmpty(city) && isEmpty(state) && isEmpty(zipcode)) && <p>{`${city}, ${state}, ${zipcode}`}</p>}
                      </>
                    ) : (
                      <TextStyle variation={'subdued'}>No address provided</TextStyle>
                    )}
                  </Card.Section>
                  <Card.Section title={'Apply data'}>
                    <Stack vertical spacing={'extraTight'}>
                      <Stack spacing={'tight'}>
                        <Stack.Item fill>Applied</Stack.Item>
                        <TextStyle variation='subdued'>{moment(createdAt).format('MMMM D, YYYY')}</TextStyle>
                      </Stack>
                      <Stack spacing={'tight'}>
                        <Stack.Item fill>Device</Stack.Item>
                        <TextStyle variation='subdued'>
                          {!!device ? device.charAt(0).toUpperCase() + device.slice(1) : 'Unknown'}
                        </TextStyle>
                      </Stack>
                      <Stack spacing={'tight'}>
                        <Stack.Item fill>Source</Stack.Item>
                        <TextStyle variation='subdued'>{source || 'Undefined'}</TextStyle>
                      </Stack>
                    </Stack>
                  </Card.Section>
                </Card>
                <Card
                  subdued
                  title={
                    <Stack spacing={'extraTight'}>
                      <Subheading>{'Tags'}</Subheading>
                    </Stack>
                  }
                  actions={[
                    {
                      content: 'View all tags',
                      onAction: toggleTagModal,
                    },
                  ]}
                  primaryFooterAction={{
                    content: 'Update tags',
                    disabled: !hasTagsChanges(),
                    onAction: () => handleUpdateCandidateTags(),
                  }}>
                  <Card.Section>
                    <Autocomplete
                      options={tagOptions}
                      selected={selectedTags}
                      textField={<Autocomplete.TextField onChange={updateText} value={inputValue} />}
                      onSelect={(value) => {
                        const selected = [...selectedTags, ...value];
                        setSelectedTags(selected.filter((el, index) => selected.indexOf(el) === index));
                      }}
                      emptyState={
                        <Button
                          plain
                          monochrome
                          icon={CirclePlusMinor}
                          fullWidth
                          textAlign={'left'}
                          onClick={handleNewTag}>{`Add ${inputValue}`}</Button>
                      }
                    />

                    <>
                      <br />
                      <Stack spacing={'tight'}>{tagsMarkup}</Stack>
                    </>
                  </Card.Section>
                </Card>
                <Card
                  subdued
                  title={
                    <Stack spacing={'extraTight'}>
                      <Subheading>{'References'}</Subheading>
                    </Stack>
                  }
                  actions={[
                    {
                      content: 'Add',
                      onAction: toggleReferenceModal,
                    },
                  ]}>
                  {isEmpty(candidateReferences || []) ? (
                    <Card.Section>
                      <TextStyle variation={'subdued'}>{'No references provided'}</TextStyle>
                    </Card.Section>
                  ) : (
                    <ResourceList
                      items={candidateReferences}
                      renderItem={(item) => {
                        const { id, first_name: firstName, last_name: lastName, phone, email, company } = item;

                        return (
                          <ResourceItem id={id}>
                            <h3>
                              <TextStyle variation='strong'>
                                {firstName} {lastName}
                              </TextStyle>
                            </h3>
                            <div>{company}</div>
                            <div>
                              <TextStyle variation='subdued'>{email}</TextStyle>
                            </div>
                            <div>
                              <TextStyle variation='subdued'>{phone}</TextStyle>
                            </div>
                          </ResourceItem>
                        );
                      }}
                    />
                  )}
                </Card>
                <Card>
                  <Card.Section actions={[{ content: 'View Job', onAction: onGoToJobPage }]} title='Job'>
                    <TextStyle>{jobTitle}</TextStyle>
                    <br />
                    <TextStyle variation='subdued'>{departmentName}</TextStyle>
                    <br />
                    <TextStyle variation='subdued'>{formattedJobCategory}</TextStyle>
                  </Card.Section>
                  <Card.Section title='LOCATION'>
                    {!job.isRemote ? (
                      <>
                        <span>{jobStreetAddress}</span>
                        <br />
                        <span>
                          {jobCity}, {jobState} {jobPostalCode}
                        </span>
                        <br />
                        <span>{jobCountry}</span>{' '}
                      </>
                    ) : (
                      <span>Remote</span>
                    )}
                  </Card.Section>
                  {!isEmpty(jobTeamMembers) && (
                    <Card.Section title='Hiring team'>
                      <Stack spacing={'tight'}>
                        {jobTeamMembers.map((member) => {
                          const { firstName, lastName, email, id } = { ...member };
                          return (
                            <TextContainer key={`member_${id}`}>
                              <p>
                                {firstName} {lastName}
                              </p>
                              <TextStyle variation='subdued'>{email}</TextStyle>
                            </TextContainer>
                          );
                        })}
                      </Stack>
                    </Card.Section>
                  )}
                </Card>
              </Layout.Section>
            </Layout>
          </Form>
          <Modal
            open={!!isModalOpen}
            onClose={() => toggleIsModalOpen()}
            title={isModalOpen === 'inactive' ? 'Disqualification reason' : null}
            primaryAction={{
              content: isModalOpen === 'inactive' ? 'Save' : 'Yes',
              onAction: onUpdateCandidateStatus,
              disabled: isUploadingData || (isModalOpen === 'inactive' && !reason),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => toggleIsModalOpen(),
                disabled: isUploadingData,
              },
            ]}
            loading={false}>
            <Modal.Section>
              {isModalOpen !== 'inactive' && (
                <>
                  <p>Are you sure you want to change this candidate status?</p>
                  {isUploadingData && (
                    <div
                      style={{
                        width: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <Spinner type='TailSpin' color='#6371c7' height={40} width={40} />
                    </div>
                  )}
                </>
              )}
              {isModalOpen === 'inactive' && (
                <>
                  <p>
                    Why are you disqualifying {`${candidateName}${!!candidateLastName ? ` ${candidateLastName}` : ''}`} from the{' '}
                    {jobTitle} position?
                  </p>
                  <br />
                  <Select
                    labelHidden={true}
                    options={dispositionCodes.map(({ id: value, name: label }) => ({ value, label }))}
                    onChange={setReason}
                    value={reason}
                    placeholder={'Select a disqualification reason'}
                  />
                </>
              )}
            </Modal.Section>
          </Modal>

          <Modal
            open={!!emailModal}
            onClose={toggleEmailModal}
            title={'Contact candidate'}
            primaryAction={{
              content: 'Send Message',
              onAction: onSendEmail,
              disabled: !(!!emailForm?.to && !!emailForm?.from && !!emailForm?.subject && !!emailForm?.message),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleEmailModal,
              },
            ]}
            loading={false}>
            <Modal.Section>
              <FormLayout>
                <Select
                  label='Email template'
                  options={externalTemplates.map((template) => {
                    const { id, title } = { ...template };
                    return {
                      value: id,
                      label: title,
                    };
                  })}
                  value={emailTemplate}
                  onChange={onChangeEmailTemplate}
                  placeholder={'Select email template'}
                />
                <FormLayout.Group>
                  <TextField
                    label='To'
                    value={emailForm?.to}
                    disabled={true}
                    onChange={(to) => setEmailForm({ ...emailForm, to })}
                  />
                  <Select
                    label='From'
                    options={(teamMembers || []).map((member) => {
                      const { firstName, lastName, email } = { ...member };
                      return {
                        value: email,
                        label: `${firstName} ${lastName} <${email}>`,
                      };
                    })}
                    onChange={(from) => setEmailForm({ ...emailForm, from })}
                    value={emailForm?.from}
                    placeholder={'Select a hiring team member'}
                  />
                </FormLayout.Group>
                <TextField
                  label='Subject'
                  value={emailForm?.subject}
                  onChange={(subject) => setEmailForm({ ...emailForm, subject })}
                />
                <Editor
                  value={emailForm?.message}
                  initialValue={emailForm?.message || ''}
                  init={{
                    height: 310,
                    menubar: false,
                    plugins: ['advlist autolink lists link', 'code fullscreen', 'wordcount'],
                    toolbar:
                      'undo redo | formatselect | fontselect | fontsizeselect | bold italic forecolor | \
                        alignleft aligncenter alignright alignjustify | \
                        bullist numlist outdent indent | removeformat | help',
                  }}
                  id='body'
                  name='body'
                  apiKey='czdl8uo4qtlzlp7ozdpws89rify9vokuyuvw21w1jr8n394z'
                  onEditorChange={(message) => setEmailForm({ ...emailForm, message })}
                />
              </FormLayout>
            </Modal.Section>
          </Modal>

          <Modal
            title={'Tags'}
            open={!!isTagModalOpen}
            onClose={() => toggleTagModal()}
            primaryAction={{
              content: 'Update tags',
              disabled: !hasTagsChanges(),
              onAction: () => handleUpdateCandidateTags(),
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => toggleTagModal(),
              },
            ]}
            loading={false}>
            <Modal.Section>
              <Subheading>Applied tags</Subheading>
              <br />
              <Stack spacing={'tight'}>{tagsMarkup}</Stack>
            </Modal.Section>
            <Modal.Section>
              <Subheading>All tags</Subheading>
              <br />
              <Stack>
                {allTagOptions.map(({ label }, index) =>
                  selectedTags.includes(label) ? (
                    <TextStyle variation={'subdued'} key={`tag_${index}`}>
                      {label}
                    </TextStyle>
                  ) : (
                    <Tag key={`tag_${index}`} onClick={() => setSelectedTags([...selectedTags, label])}>
                      {label}
                    </Tag>
                  )
                )}
              </Stack>
            </Modal.Section>
          </Modal>

          <Modal
            open={addressModal}
            onClose={toggleAddressModal}
            title={`${hasMinAddress ? 'Edit' : 'Add'} address`}
            primaryAction={{
              content: hasMinAddress ? 'Update' : 'Save',
              disabled:
                isEmpty(addressForm?.address) ||
                isEmpty(addressForm?.city) ||
                isEmpty(addressForm?.state) ||
                isEmpty(addressForm?.zipcode),
              onAction: onUpdateCandidateAddress,
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: toggleAddressModal,
              },
            ]}
            loading={isUploadingData}>
            <Modal.Section>
              <FormLayout>
                <TextField
                  id='address1'
                  label='Address'
                  value={addressForm.address}
                  name='address1'
                  onChange={(address) => setAddressForm({ ...addressForm, address })}
                />
                <FormLayout.Group>
                  <div>
                    <TextField
                      id='address2'
                      label='Apartment, suite, etc'
                      value={addressForm.unit}
                      name='address2'
                      onChange={(unit) => setAddressForm({ ...addressForm, unit })}
                    />
                  </div>
                  <div>
                    <TextField
                      id='city'
                      label='City'
                      value={addressForm.city}
                      name='city'
                      onChange={(city) => setAddressForm({ ...addressForm, city })}
                    />
                  </div>
                </FormLayout.Group>
                <FormLayout.Group>
                  <div>
                    <TextField
                      id='state'
                      label='State'
                      value={addressForm.state}
                      name='state'
                      onChange={(state) => setAddressForm({ ...addressForm, state })}
                    />
                  </div>
                  <div>
                    <TextField
                      id='zipcode'
                      label='Zip code'
                      value={addressForm.zipcode}
                      name='zipCode'
                      onChange={(zipcode) => setAddressForm({ ...addressForm, zipcode })}
                    />
                  </div>
                </FormLayout.Group>
              </FormLayout>
            </Modal.Section>
          </Modal>

          <Modal
            title={'Add reference'}
            open={!!referenceModal}
            onClose={() => toggleReferenceModal()}
            primaryAction={{
              content: 'Add reference',
              disabled:
                !referenceForm.firstName ||
                !referenceForm.lastName ||
                !referenceForm.company ||
                !referenceForm.email ||
                !validateEmail(referenceForm.email) ||
                !referenceForm.phone,
              onAction: onCreateReference,
            }}
            secondaryActions={[
              {
                content: 'Cancel',
                onAction: () => toggleReferenceModal(),
              },
            ]}
            loading={false}>
            <Modal.Section>
              <FormLayout>
                <FormLayout.Group>
                  <TextField
                    label='First name'
                    value={referenceForm?.firstName}
                    onChange={(firstName) => setReferenceForm({ ...referenceForm, firstName })}
                  />
                  <TextField
                    label='Last name'
                    value={referenceForm?.lastName}
                    onChange={(lastName) => setReferenceForm({ ...referenceForm, lastName })}
                  />
                </FormLayout.Group>
                <TextField
                  label='Company'
                  value={referenceForm?.company}
                  onChange={(company) => setReferenceForm({ ...referenceForm, company })}
                />
                <FormLayout.Group>
                  <TextField
                    label='Phone'
                    value={referenceForm?.phone}
                    onChange={(phone) => setReferenceForm({ ...referenceForm, phone })}
                  />
                  <TextField
                    label='Email'
                    value={referenceForm?.email}
                    onChange={(email) => setReferenceForm({ ...referenceForm, email })}
                  />
                </FormLayout.Group>
              </FormLayout>
            </Modal.Section>
          </Modal>

          {toastStatus.status && (
            <Toast content={toastStatus.message} onDismiss={() => setToastStatus({ status: false, message: '' })} />
          )}
          {isUploadingData && <Loader secondary={true} />}
        </Page>
        <Footer link='https://help.shophire.co/article/5-candidates' name='Candidates' />
      </Frame>
    </AppProvider>
  );
};

export default Candidate;
